/**
 * LineScore Component
 */

import React, { ReactElement } from 'react';
import { useTheme } from 'styled-components';
import { LineScoreProps, LineScoreTypes, InningData, TotalsData, LineScoreTextTypes } from './lineScore.types';

import Table from '../Table/table';
import TeamLogo from '../TeamLogo/teamLogo';
import { TeamLogoTypes } from '../TeamLogo/teamLogo.types';
import { BodyFootProps, Alignments, HeadProps } from '../Table/table.types';
import { LinkProps } from '../Link/link.types';
import { StyledHeadCell, StyledInningCell, LineScoreWrapper, SplitSquadWrapper } from './lineScore.style';
const { SPACER, INNING_NOT_PLAYED } = LineScoreTextTypes;
import wrapLink from '../../utils/linkWrapperHelper';

// Build a cell to appear in the body of the linescore
const buildBodyCell = (
  extraRowSpacing: string,
  content: string | number | ReactElement,
  inningsType: boolean,
  dark: boolean,
  runsCol: boolean = false,
): ReactElement => (
  <StyledInningCell paddingTop={extraRowSpacing} inningsType={inningsType} dark={dark} runs={runsCol}>
    {content}
  </StyledInningCell>
);

// Build the data to appear in the totals section
const buildTotalsData = (
  extraRowSpacing: string,
  totalsData: TotalsData,
  complete: boolean,
  dark: boolean,
  headerAbbr: string[],
): [HeadProps[], BodyFootProps[]] => {
  // Set the header data for the totals table
  const headData: HeadProps[] = [
    {
      content: <StyledHeadCell dark={dark}>{headerAbbr[0]}</StyledHeadCell>,
      dataField: 'runs',
      fixedWidth: '10px',
      highlighted: complete,
    },
    {
      content: <StyledHeadCell dark={dark}>{headerAbbr[1]}</StyledHeadCell>,
      dataField: 'hits',
      fixedWidth: '10px',
    },
    {
      content: <StyledHeadCell dark={dark}>{headerAbbr[2]}</StyledHeadCell>,
      dataField: 'errors',
      fixedWidth: '10px',
    },
  ];

  // Build the data for the body of the totals section
  const bodyData: BodyFootProps[] = [];
  for (let i: number = 0; i < 2; i++) {
    const isHome: boolean = i % 2 === 1;
    bodyData.push({
      runs: buildBodyCell(
        isHome ? extraRowSpacing : '0px',
        isHome ? totalsData.home.runs : totalsData.away.runs,
        false,
        dark,
        true,
      ),
      hits: buildBodyCell(
        isHome ? extraRowSpacing : '0px',
        isHome ? totalsData.home.hits : totalsData.away.hits,
        false,
        dark,
      ),
      errors: buildBodyCell(
        isHome ? extraRowSpacing : '0px',
        isHome ? totalsData.home.errors : totalsData.away.errors,
        false,
        dark,
      ),
    });
  }

  return [headData, bodyData];
};

// Build the data for the innings section
const buildInningsData = (
  extraRowSpacing: string,
  awayName: string,
  homeName: string,
  inningsData: InningData[],
  maxInningsDisplayed: number,
  complete: boolean,
  collapsed: boolean,
  dark: boolean,
  inningsFixedWidth: string,
  awayTeamId?: number,
  homeTeamId?: number,
  awayLinkProps?: LinkProps,
  homeLinkProps?: LinkProps,
  awaySplitSquad?: string,
  homeSplitSquad?: string,
  teamLogoType?: TeamLogoTypes,
): [HeadProps[], BodyFootProps[]] => {
  // Start the header data with a blank one for the teams
  const headData: HeadProps[] = [
    {
      content: <br />,
      dataField: 'teamName',
      alignment: Alignments.LEFT,
      fixedWidth: '35px',
      pinned: true,
    },
  ];

  // Start the body data with the teamnames already in it
  const bodyData: BodyFootProps[] = [
    {
      teamName: (
        <StyledInningCell team dark={dark} inningsType>
          {wrapLink(
            awayTeamId ? (
              <>
                <TeamLogo teamId={awayTeamId} teamLogoType={teamLogoType} alt={awayName} width={24} />
                {awaySplitSquad && <SplitSquadWrapper>{awaySplitSquad}</SplitSquadWrapper>}
              </>
            ) : (
              awayName
            ),
            awayLinkProps,
          )}
        </StyledInningCell>
      ),
    },
    {
      teamName: (
        <StyledInningCell team dark={dark} inningsType paddingTop={extraRowSpacing}>
          {wrapLink(
            homeTeamId ? (
              <>
                <TeamLogo teamId={homeTeamId} teamLogoType={teamLogoType} alt={homeName} width={24} />
                {homeSplitSquad && <SplitSquadWrapper>{homeSplitSquad}</SplitSquadWrapper>}
              </>
            ) : (
              homeName
            ),
            homeLinkProps,
          )}
        </StyledInningCell>
      ),
    },
  ];

  // Build the data for each inning
  for (let i: number = 1; i < maxInningsDisplayed + 1; i++) {
    const inningData: InningData = inningsData[i - 1];
    // Build the header
    const headObject: HeadProps = {
      content: <StyledHeadCell dark={dark}>{i}</StyledHeadCell>,
      dataField: i.toString(),
      fixedWidth: collapsed ? inningsFixedWidth : '100%',
    };

    let awayContent: number | string | undefined = SPACER;
    let homeContent: number | string | undefined = SPACER;
    // If there is passed inning data, use it
    if (inningData) {
      awayContent = inningData.away && inningData.away.runs;
      homeContent = inningData.home && inningData.home.runs;

      if (complete) {
        if (typeof awayContent === 'undefined') {
          awayContent = INNING_NOT_PLAYED;
        }
        if (typeof homeContent === 'undefined') {
          homeContent = INNING_NOT_PLAYED;
        }
      }
    }

    // Build the home and away inning data
    bodyData[0][i] = buildBodyCell('0px', awayContent as string | number, true, dark);
    bodyData[1][i] = buildBodyCell(extraRowSpacing, homeContent as string | number, true, dark);

    headData.push(headObject);
  }

  // If the table isn't collapsed, add a column to fill the rest of the space
  if (!collapsed) {
    headData.push({
      dataField: 'INNING_TABLE_SPACE',
      content: '',
      fixedWidth: '5px',
    });
  }

  return [headData, bodyData];
};

const LineScore = ({
  type = LineScoreTypes.FULL,
  awayName,
  homeName,
  complete,
  currentInning,
  inningHalf,
  scheduledInnings = 9,
  inningsData,
  totalsData,
  maxWidth = '100%',
  collapsedUntilInning = 9,
  extraRowSpacing = '0px',
  headerAbbr = ['R', 'H', 'E'],
  inningsFixedWidth = '19px',
  maxInningsInView = 9,
  awayTeamId,
  homeTeamId,
  logoHeight = '24px',
  logoOffset = '-3px',
  homeLinkProps,
  awayLinkProps,
  homeSplitSquad,
  awaySplitSquad,
  teamLogoType = TeamLogoTypes.CAP,
  className,
  ...props
}: LineScoreProps) => {
  // Determine the max inning to display
  let maxInningsDisplayed: number = scheduledInnings;
  if (inningsData) {
    maxInningsDisplayed = Math.max(maxInningsDisplayed, inningsData.length);
  }

  // If collapsed is true, but we're past the passed inning, set collapsed to false
  let { collapsed = false } = props;
  if (collapsed && maxInningsDisplayed >= collapsedUntilInning) {
    collapsed = false;
  }

  // Retrieve the dark boolean from theme
  const { dark = false } = useTheme();

  // Determine if we're using a full table
  const fullTable: boolean = type === LineScoreTypes.FULL && !!inningsData;

  // Build the left innings table if it's type FULL and has the data
  let inningsTable: ReactElement | null = null;
  if (fullTable) {
    // Build the data for the innings table
    const data: [HeadProps[], BodyFootProps[]] = buildInningsData(
      extraRowSpacing,
      awayName,
      homeName,
      inningsData as InningData[],
      maxInningsDisplayed,
      !!complete,
      collapsed,
      dark,
      inningsFixedWidth,
      awayTeamId,
      homeTeamId,
      awayLinkProps,
      homeLinkProps,
      awaySplitSquad,
      homeSplitSquad,
      teamLogoType,
    );

    // Highlight the current inning cell
    const highlightedCell: { dataField: string; rowIndex: number }[] = [];
    if (!complete && typeof currentInning !== 'undefined' && inningHalf) {
      const rowIndex: number = inningHalf === 'bottom' ? 1 : 0;

      highlightedCell.push({
        dataField: (currentInning || 1).toString(),
        rowIndex,
      });
    }

    inningsTable = (
      <Table
        hideDividers
        tableFixed={!collapsed}
        startRight
        headThPadding="5px 0px 1px 0px"
        bodyThPadding="3px 0px"
        bodyTdPadding="3px 0px"
        maxWidth={`calc(${maxWidth} - 70px)`}
        width={collapsed ? '' : '100%'}
        thead={data[0]}
        tbody={data[1]}
        highlightedCells={highlightedCell}
      />
    );
  }

  // Build the totals data
  const tData: [HeadProps[], BodyFootProps[]] = buildTotalsData(
    extraRowSpacing,
    totalsData,
    complete as boolean,
    dark,
    headerAbbr,
  );

  // set default table width (in inning columns) to be the scheduled innings
  let tableWidthInInnings = scheduledInnings;
  // Determine the max width we're willing to look at for the linescore
  const maxInningWidth = Math.max(scheduledInnings, maxInningsInView);
  // If the innings we have to display are greater than scheduled innings (in extra innings)
  // and the num we have are less than the max we're willing to go (Ex: in 8th inning with 7 scheduled and 9 max willing at once),
  // we'll display 8
  if (maxInningsDisplayed > scheduledInnings && maxInningsDisplayed < maxInningWidth) {
    tableWidthInInnings = maxInningsDisplayed;
    // If the innings we have are more than the max, set the width shown at once to the max
  } else if (maxInningsDisplayed > maxInningWidth) {
    tableWidthInInnings = maxInningWidth;
  }

  return (
    <LineScoreWrapper
      maxWidth={maxWidth}
      scheduledInnings={tableWidthInInnings}
      maxInningsDisplayed={maxInningsDisplayed}
      logoHeight={logoHeight}
      logoOffset={logoOffset}
      className={className}
    >
      {inningsTable}
      <Table
        hideDividers
        headThPadding="5px 6px 1px 5px"
        bodyThPadding="3px 0px"
        bodyTdPadding="3px 0px"
        width={'67px'}
        thead={tData[0]}
        tbody={tData[1]}
        highlightHead={false}
      />
    </LineScoreWrapper>
  );
};

export default LineScore;
